import { DestinationEvent } from '@eventbrite/event-renderer';
import { useGate } from '@eventbrite/statsig';
import React from 'react';
import {
    Breadcrumb,
    ThingsToDoBreadcrumbs,
} from '../../containers/ThingsToDoBreadcrumbs';
import { NearbyPOITagShape } from '../../types/AppProps';
import { AboutPOI } from './containers/AboutPOI';
import { EventSection } from './containers/EventSection';
import { NearbyPOI } from './containers/NearbyPOI';
import {
    POIHeroSection,
    POIHeroSectionProps,
} from './containers/POIHeroSection';
import { PointsOfInterestFAQs } from './containers/PointsOfInterestFAQs';
import styles from './PointOfInterestPage.module.scss';

export interface PointOfInterest {
    pointOfInterestName: string;
    geometry: {
        latitude: number;
        longitude: number;
    };
    types: string[];
    photos: Object[];
    googlePlaceId: string;
    description: string;
    address: {
        formattedAddress: string;
        city: string;
        state: string;
        country: string;
    };
    htmlAttribution: string;
    imageSource: string;
    hoursOfOperation: Array<string>;
    popularEvents: Array<{
        eventName: string;
        eventSlug: string;
    }>;
}

export interface PointOfInterestProps {
    pointOfInterest: PointOfInterest;
    heroSection: POIHeroSectionProps;
    breadcrumbs: Breadcrumb[];
    nearbyPointsOfInterest: Array<NearbyPOITagShape>;
    nearbyEvents: Array<DestinationEvent>;
}

export const PointOfInterestPage: React.FunctionComponent<
    PointOfInterestProps
> = (props) => {
    const {
        pointOfInterest,
        nearbyPointsOfInterest,
        breadcrumbs,
        nearbyEvents,
    } = props;

    const enableNearbyPOISection = useGate('enable_poi_nearby_section').value;

    const enablePOIFAQSection = useGate('enable_poi_faq_section').value;

    return (
        <>
            <div className={styles.topSection}>
                <div className={styles.headerBackground} />
                <ThingsToDoBreadcrumbs
                    containerClasses={styles.breadcrumbs}
                    breadcrumbs={breadcrumbs}
                />
                <POIHeroSection
                    pointOfInterestName={pointOfInterest.pointOfInterestName}
                    imageSource={pointOfInterest.imageSource}
                    htmlAttribution={pointOfInterest.htmlAttribution}
                />
            </div>
            <AboutPOI
                title={pointOfInterest.pointOfInterestName}
                content={pointOfInterest.description}
                address={pointOfInterest.address?.formattedAddress}
                openingHours={pointOfInterest.hoursOfOperation}
            />
            <EventSection
                pointOfInterestName={pointOfInterest.pointOfInterestName}
                nearbyEvents={nearbyEvents}
            />
            <div className={styles.bottomSection}>
                {enableNearbyPOISection && (
                    <NearbyPOI
                        pointOfInterestName={
                            pointOfInterest.pointOfInterestName
                        }
                        nearbyPointsOfInterest={nearbyPointsOfInterest}
                    />
                )}
                {enablePOIFAQSection && (
                    <PointsOfInterestFAQs
                        pointOfInterestName={
                            pointOfInterest.pointOfInterestName
                        }
                        hoursOfOperation={pointOfInterest.hoursOfOperation}
                        nearbyPointsOfInterest={nearbyPointsOfInterest}
                        nearbyEvents={nearbyEvents}
                    />
                )}
            </div>
        </>
    );
};
